import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { Skeleton } from '@/components/ui/skeleton';
import type { DataFetchParams } from '@/components/DataTable';
import type {
  MCPEnforcementView,
  MCPServerListItem,
} from '@kirin/api-client/contracts/mcp';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  useCanViewAdminContent,
  useIsOwnerOrManager,
} from '@/hooks/useRoleCheck';
import { useMcpGatewayControlEnabled } from '@/hooks/useMcpGatewayControlEnabled';
import { useMcpInventoryEnforcementEnabled } from '@/hooks/useMcpInventoryEnforcementEnabled';
import { useAgentmeshMcpRiskEnabled } from '@/hooks/useAgentmeshMcpRiskEnabled';
import { MCPUserListModal } from '../../components/MCPUserListModal';
import { createMCPColumns, type McpEnforcementColumnOptions } from './columns';
import { fetchMCPServers, type MCPTableMeta } from './api';
import { MCP_TABLE_CONFIG } from './constants';
import { MCPSourceFilterButtons } from './MCPSourceFilterButtons';
import { applySourceFilter } from './sourceFilter';
import { useMcpSourceFilter } from './useMcpSourceFilter';
import { useGatewayControlToggle } from './useGatewayControlToggle';
import {
  DEFAULT_GATEWAY_SYNC_STATE,
  type BlockConfirmTarget,
  type GatewayControlCallbacks,
} from './gatewayControlUtils';
import { GatewaySyncFooter } from './GatewaySyncFooter';
import { BlockConfirmationDialog } from './BlockConfirmationDialog';
import { GatewayToolListExpansion } from './GatewayToolListExpansion';
import { useMcpEnforcementView } from './useMcpEnforcementView';
import { useMcpEnforcementToggle } from './useMcpEnforcementToggle';
import { McpEnforcementFilterChips } from './McpEnforcementFilterChips';
import { NewMcpServersBanner } from './NewMcpServersBanner';
import { splitEnforcedAssistants } from './endpointEnforcement';

function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams,
  enforcementView: MCPEnforcementView | null = null
) {
  return queryKeys.mcp.dataTable(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
    enforcementView,
  });
}

interface DataTableMCPContentProps {
  organizationId: string;
}

export function DataTableMCPContent({
  organizationId,
}: DataTableMCPContentProps): ReactElement {
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const { canViewAdminContent, isLoading: isAdminViewLoading } =
    useCanViewAdminContent(organizationId);
  const gatewayControlEnabled = useMcpGatewayControlEnabled();
  const { source, setSource } = useMcpSourceFilter();
  const { toggleServer, toggleTool } = useGatewayControlToggle();
  const [blockConfirmTarget, setBlockConfirmTarget] =
    useState<BlockConfirmTarget | null>(null);
  const [meta, setMeta] = useState<MCPTableMeta | null>(null);

  // KIR-4482. Independent of gateway control: endpoints enforce Inventory
  // blocks whenever this flag is on, so the UI to see and lift them must show
  // too. With both on, endpoint rows carry the Enforcement column and gateway
  // rows the Gateway Control column; each renders nothing on the other's rows.
  const enforcementFlag = useMcpInventoryEnforcementEnabled();
  const enforcementEnabled = enforcementFlag.enabled;
  // While the flag is still loading, keep asking for the URL's view (the
  // backend ignores it if the flag is off), so a deep link to a New/Blocked
  // page neither flashes the unfiltered list nor loses its page.
  const enforcementRequested =
    enforcementFlag.enabled || enforcementFlag.isLoading;
  const { isOwnerOrManager } = useIsOwnerOrManager(organizationId);
  const { view: urlView, setView } = useMcpEnforcementView();
  // The chips are admin-only, so a member never filters by a `?ev` they
  // cannot see or clear. While the role is still loading the URL's view is
  // kept, so an admin's deep link does not flash the unfiltered list.
  const enforcementView =
    enforcementRequested && (canViewAdminContent || isAdminViewLoading)
      ? urlView
      : null;
  const { setBlocked, isPending } = useMcpEnforcementToggle(organizationId);
  // KIR-5108 AgentMesh Risk column. Fails closed; the backend strips a risk
  // filter/sort from a stale URL while the flag is off.
  const { enabled: riskEnabled, isLoading: riskFlagLoading } =
    useAgentmeshMcpRiskEnabled();
  const enforcementSummary = meta?.enforcementSummary;

  const enforcementColumns = useMemo<McpEnforcementColumnOptions | undefined>(
    () =>
      enforcementEnabled
        ? {
            canEdit: isOwnerOrManager,
            isPending,
            onAllow: row => void setBlocked(row.name, false),
            onRequestBlock: row => {
              const { enforced, notEnforced } = splitEnforcedAssistants(
                row.sources
              );
              setBlockConfirmTarget({
                kind: 'endpointServer',
                serverName: row.name,
                enforcedAssistants: enforced,
                notEnforcedAssistants: notEnforced,
                userCount: row.userCount ?? 0,
              });
            },
          }
        : undefined,
    [enforcementEnabled, isOwnerOrManager, isPending, setBlocked]
  );

  // `useMutation` hands back a fresh result object on every render, so depending
  // on `toggleServer`/`toggleTool` themselves would invalidate `gatewayControl`
  // — and therefore `columns` — every render, rebuilding the whole table in a
  // loop. The `.mutate` functions are referentially stable; depend on those.
  const { mutate: mutateServer } = toggleServer;
  const { mutate: mutateTool } = toggleTool;

  // Likewise, key off the applied version itself rather than the `meta` object:
  // each 10s poll returns a new `lastContactAt`, and depending on the object
  // would rebuild every column on every tick for a value the columns never read.
  const appliedPolicyVersion = meta?.gatewaySync?.appliedPolicyVersion ?? null;

  const gatewayControl = useMemo<GatewayControlCallbacks | undefined>(
    () =>
      gatewayControlEnabled
        ? {
            appliedPolicyVersion,
            onAllowServer: serverId =>
              mutateServer({ serverId, blocked: false }),
            onAllowTool: (serverId, toolId) =>
              mutateTool({ serverId, toolId, blocked: false }),
            onRequestBlockConfirmation: setBlockConfirmTarget,
          }
        : undefined,
    [gatewayControlEnabled, appliedPolicyVersion, mutateServer, mutateTool]
  );

  const columns = useMemo(
    () =>
      createMCPColumns(
        organizationId,
        canViewAdminContent ? setSelectedServer : undefined,
        gatewayControlEnabled,
        gatewayControl,
        enforcementColumns,
        riskEnabled
      ),
    [
      organizationId,
      canViewAdminContent,
      gatewayControlEnabled,
      gatewayControl,
      enforcementColumns,
      riskEnabled,
    ]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchMCPServers(
        organizationId,
        applySourceFilter(params, gatewayControlEnabled ? source : null),
        signal,
        enforcementRequested ? { view: enforcementView } : undefined
      ),
    [
      organizationId,
      gatewayControlEnabled,
      source,
      enforcementRequested,
      enforcementView,
    ]
  );

  const getQueryKeys = useCallback(
    (params: DataFetchParams) =>
      getQueryKeysFromParams(
        organizationId,
        applySourceFilter(params, gatewayControlEnabled ? source : null),
        enforcementView
      ),
    [organizationId, gatewayControlEnabled, source, enforcementView]
  );

  const handleConfirmBlock = useCallback(() => {
    if (!blockConfirmTarget) return;
    if (blockConfirmTarget.kind === 'endpointServer') {
      void setBlocked(blockConfirmTarget.serverName, true);
    } else if (blockConfirmTarget.kind === 'server') {
      mutateServer({
        serverId: blockConfirmTarget.serverId,
        blocked: true,
      });
    } else {
      mutateTool({
        serverId: blockConfirmTarget.serverId,
        toolId: blockConfirmTarget.toolId,
        blocked: true,
      });
    }
    setBlockConfirmTarget(null);
  }, [blockConfirmTarget, mutateServer, mutateTool, setBlocked]);

  // Stable identities: a new object here would rebuild the table every render.
  //
  // Sorting and filtering are ALWAYS on (KIR-5042). They were switched off with
  // the gateway-control flag only because the fixture read path could not honour
  // them; the real endpoint has sorted and filtered both union branches since
  // KIR-3940 slice 2b, so the flag now changes only which columns appear.
  const features = useMemo(
    () => ({
      enableSorting: true,
      enableFiltering: true,
      enableRowExpansion: gatewayControlEnabled,
    }),
    [gatewayControlEnabled]
  );

  const rowExpansion = useMemo(
    () =>
      gatewayControlEnabled && gatewayControl
        ? {
            // Matches the union endpoint's `${provenance}:${id ?? name}` row
            // identity, so a gateway row and an endpoint row sharing a name
            // never collide in expansion state.
            getRowId: (row: MCPServerListItem) =>
              `${row.provenance ?? 'endpoint'}:${row.id ?? row.name}`,
            canExpand: (row: MCPServerListItem) => row.provenance === 'gateway',
            keepMounted: false,
            render: (row: MCPServerListItem) => (
              <GatewayToolListExpansion row={row} callbacks={gatewayControl} />
            ),
          }
        : undefined,
    [gatewayControlEnabled, gatewayControl]
  );

  const countScope = useMemo(
    () => ({
      organizationId,
      source: gatewayControlEnabled ? source : null,
      enforcementView,
    }),
    [organizationId, gatewayControlEnabled, source, enforcementView]
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 30000,
      refetchOnWindowFocus: false,
      // Fires only while the tab is visible — TanStack Query's
      // refetchIntervalInBackground default is false.
      refetchInterval: gatewayControlEnabled ? 10_000 : undefined,
    }),
    [gatewayControlEnabled]
  );

  // Search is ALWAYS offered (KIR-5042). It was omitted with the
  // gateway-control flag only because the fixture read path ignored it; the
  // real endpoint has searched both union branches since KIR-3940 slice 2b.
  // A constant rather than a bare literal in the JSX: a new object each render
  // would rebuild the table (see `features` above).
  const search = useMemo(
    () => ({ placeholder: 'Search MCP servers...', className: 'w-[260px]' }),
    []
  );

  // Org-wide New counts are an admin view; members see only their own rows.
  const showEnforcementSummary = enforcementEnabled && canViewAdminContent;
  const newCount = enforcementSummary?.newCount ?? 0;

  return (
    <>
      {showEnforcementSummary && (
        <div className="flex flex-col gap-3 px-4 py-2 shrink-0">
          {newCount > 0 && (
            <NewMcpServersBanner
              newCount={newCount}
              newWindowDays={enforcementSummary?.newWindowDays ?? 7}
              onReviewNew={() => setView('new')}
            />
          )}
          <McpEnforcementFilterChips
            view={enforcementView}
            summary={enforcementSummary}
            onChange={setView}
          />
        </div>
      )}

      {gatewayControlEnabled && (
        <div className="flex items-center px-4 py-2 shrink-0">
          <MCPSourceFilterButtons source={source} onChange={setSource} />
        </div>
      )}

      {/* The DataTable reads its URL state (filters, sort) once, at mount,
          against the columns it has then. Mounting it before the risk flag
          resolves would drop a deep-linked `rk` filter or risk sort and
          rewrite the URL without it, so hold it until the flag is known. */}
      {riskFlagLoading ? (
        <div data-testid="mcp-risk-flag-loading" className="px-4 py-2">
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <DataTable<MCPServerListItem, MCPTableMeta>
          id={MCP_TABLE_CONFIG.tableId}
          mode="server"
          className="flex flex-col flex-1 min-h-0"
          containerClassName="flex-1"
          columns={columns}
          errorBoundaryLabel="Something went wrong in the MCP servers table"
          search={search}
          features={features}
          rowExpansion={rowExpansion}
          queryFn={queryFn}
          getQueryKeysFromParams={getQueryKeys}
          onMeta={setMeta}
          noDataLabel="No MCP servers found."
          countScope={countScope}
          queryConfig={queryConfig}
          // Keyed on the chip the user picked (the URL), so the role or flag
          // resolving after mount keeps an admin's deep-linked page. Only when
          // that gating drops the URL's view (a member, or the flag off) does
          // the page reset: the list is then unfiltered.
          pageResetKey={`${source ?? ''}|${urlView ?? ''}|${
            urlView !== null && enforcementView === null ? 'ignored' : ''
          }`}
        />
      )}

      {gatewayControlEnabled && (
        <GatewaySyncFooter
          gatewaySync={meta?.gatewaySync ?? DEFAULT_GATEWAY_SYNC_STATE}
        />
      )}

      {(gatewayControlEnabled || enforcementEnabled) && (
        <BlockConfirmationDialog
          target={blockConfirmTarget}
          onCancel={() => setBlockConfirmTarget(null)}
          onConfirm={handleConfirmBlock}
        />
      )}

      {canViewAdminContent && (
        <MCPUserListModal
          organizationId={organizationId}
          serverName={selectedServer}
          onClose={() => setSelectedServer(null)}
        />
      )}
    </>
  );
}

DataTableMCPContent.displayName = 'DataTableMCPContent';

export default function DataTableMCPTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  if (!organizationId) {
    return null;
  }

  return (
    <DataTableMCPContent key={organizationId} organizationId={organizationId} />
  );
}
